/**
 * The immutable workflow history for one alert, newest first.
 *
 * Backend-owned and append-only — nothing here edits, merges or collapses
 * entries, and review cycles are NOT folded together: an alert reopened and
 * re-decided is exactly the history an auditor needs to see in full.
 */
import { useTranslation } from "react-i18next";
import { Bot, User } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { describeActivityKind, describeActor } from "@/alerts/utils/activity-principal";
import { readDispositionLabel } from "@/alerts/utils/dispositions";
import { formatSiteTime } from "@/alerts/utils/format";
import { alertStatusLabel, AlertStatus } from "@/alerts/types/machine-alert";
import type { AlertActivity } from "@/alerts/types/alerts-workspace";

const STATUS_STYLE: Record<string, string> = {
  [AlertStatus.Pending]: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  [AlertStatus.Resolved]:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  [AlertStatus.Ignored]: "border-zinc-500/30 bg-zinc-500/10 text-zinc-600 dark:text-zinc-300",
};

/**
 * Status is never carried by color alone — the chip always states the status in
 * words, so it survives a color-blind operator and a monochrome screen alike.
 */
const StatusChip = ({ status }: { status: AlertStatus }) => (
  <span
    className={cn(
      "rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
      STATUS_STYLE[status] ?? "border-border text-muted-foreground",
    )}
  >
    {alertStatusLabel(status)}
  </span>
);

export const AlertActivityTimeline = ({
  activities,
  isLoading,
  isError,
  timezone,
}: {
  activities: AlertActivity[];
  isLoading: boolean;
  isError: boolean;
  timezone: string | null;
}) => {
  const { t } = useTranslation("alerts");

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label={t("activity.loading")}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-xs text-muted-foreground">{t("activity.error")}</p>
    );
  }
  if (activities.length === 0) {
    return <p className="text-xs text-muted-foreground">{t("activity.empty")}</p>;
  }

  return (
    <ol className="space-y-3">
      {activities.map((activity) => {
        const actor = describeActor(activity.principal);
        const ActorIcon = actor.isPerson ? User : Bot;
        const disposition = readDispositionLabel(activity.disposition_after);
        return (
          <li key={activity.uuid} className="flex gap-2.5">
            <span
              aria-hidden
              className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-muted"
            >
              <ActorIcon className="h-3 w-3 text-muted-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium">{describeActivityKind(activity.kind)}</span>
                {/* Chips read the AFTER state — what the alert became. */}
                {activity.status_after && <StatusChip status={activity.status_after} />}
                {disposition && (
                  <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {disposition}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {actor.label} · {formatSiteTime(activity.created_at, timezone)}
                {activity.review_cycle > 0
                  ? ` · ${t("activity.cycle", { n: activity.review_cycle })}`
                  : ""}
              </p>
              {activity.comment && (
                <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted/60 px-2 py-1.5 text-xs">
                  {activity.comment}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
